import { leadPipelineUi, type InspectionLeadRow } from '@/types/inspection-lead'
import { formatPhoneDisplay } from '@/utils/phone'
import i18n from '@/utils/i18n'

/** Общий стиль карточки-секции в деталке лида. */
export const leadCardCls =
  'rounded-xl border border-[var(--stryzh-color-border-default)] bg-[var(--stryzh-color-surface)] p-4'

/** Редактируемые поля карточки лида (строки формы). */
export interface LeadDraft {
  client_name: string
  client_phone: string
  location: string
  vehicle_brand: string
  vehicle_model: string
  vehicle_year: string
  vehicle_vin: string
  vehicle_license_plate: string
  seller_name: string
  seller_phone: string
  item_id: string
  item_link: string
  price_amount: string
  inspection_address: string
  qualification_note: string
  partner_comment: string
}

export function toLeadDraft(l: InspectionLeadRow): LeadDraft {
  return {
    client_name: l.client_name ?? '',
    client_phone: l.client_phone ? formatPhoneDisplay(l.client_phone) : '',
    location: l.location ?? '',
    vehicle_brand: l.vehicle_brand ?? '',
    vehicle_model: l.vehicle_model ?? '',
    vehicle_year: l.vehicle_year != null ? String(l.vehicle_year) : '',
    vehicle_vin: l.vehicle_vin ?? '',
    vehicle_license_plate: l.vehicle_license_plate ?? '',
    seller_name: l.seller_name ?? '',
    seller_phone: l.seller_phone ? formatPhoneDisplay(l.seller_phone) : '',
    item_id: l.item_id != null ? String(l.item_id) : '',
    item_link: l.item_link ?? '',
    price_amount: l.price_amount != null ? String(l.price_amount) : '',
    inspection_address: l.inspection_address ?? '',
    qualification_note: l.qualification_note ?? '',
    partner_comment: l.partner_comment ?? '',
  }
}

/** Diff draft → patch (только изменившиеся поля). */
export function diffLeadDraft(draft: LeadDraft, lead: InspectionLeadRow): Record<string, unknown> {
  const patch: Record<string, unknown> = {}
  const set = (key: keyof LeadDraft, db: keyof InspectionLeadRow, parse?: (v: string) => unknown) => {
    const raw = draft[key].trim()
    const next = raw === '' ? null : parse ? parse(raw) : raw
    if (next !== lead[db]) patch[db] = next
  }
  set('client_name', 'client_name')
  // В поле телефон в маске «+7 985 310 8030», в БД — «+79853108030».
  const phone = (s: string) => formatPhoneDisplay(s).replace(/\s/g, '')
  set('client_phone', 'client_phone', phone)
  set('location', 'location')
  set('vehicle_brand', 'vehicle_brand')
  set('vehicle_model', 'vehicle_model')
  set('vehicle_year', 'vehicle_year', (s) => {
    const n = Number(s)
    return Number.isFinite(n) ? n : null
  })
  set('vehicle_vin', 'vehicle_vin')
  set('vehicle_license_plate', 'vehicle_license_plate')
  set('seller_name', 'seller_name')
  set('seller_phone', 'seller_phone', phone)
  set('item_id', 'item_id', (s) => {
    const n = Number(s)
    return Number.isFinite(n) && n > 0 ? n : null
  })
  set('item_link', 'item_link')
  set('inspection_address', 'inspection_address')
  set('qualification_note', 'qualification_note')
  set('partner_comment', 'partner_comment')
  // Цена редактируется только до оплаты; пустое не превращаем в null (NOT NULL).
  if (!lead.payment_at) {
    const priceRaw = draft.price_amount.trim()
    if (priceRaw !== '') {
      const n = Number(priceRaw)
      if (Number.isFinite(n) && n >= 0 && Math.round(n) !== lead.price_amount) {
        patch.price_amount = Math.round(n)
      }
    }
  }
  // payment_amount / payment_at / tbank_payment_id / refunded_at — read-only,
  // immutable на стороне БД (триггер). Источник правды — webhook от Т-Банка
  // и edge function tbank-refund-payment. См. блок «Платёж» в UI.
  return patch
}

export function isAutotekaWaiting(lead: Pick<InspectionLeadRow, 'autoteka_state'>): boolean {
  return lead.autoteka_state === 'preview_pending' || lead.autoteka_state === 'report_pending'
}

const VIN17_RE = /^[A-HJ-NPR-Z0-9]{17}$/i

/** Есть по чему искать в Автотеке: ID объявления (Avito), VIN или госномер. */
export function hasAutotekaVehicleId(lead: InspectionLeadRow): boolean {
  return (
    (leadPipelineUi(lead.pipeline).sections.itemId && lead.item_id != null) ||
    VIN17_RE.test((lead.vehicle_vin ?? '').trim()) ||
    !!lead.vehicle_license_plate?.trim()
  )
}

/** Человеческие тексты ошибок обогащения Автотекой. Сырые reason / message из
 *  edge функции / supabase-js оператору ничего не говорят — сводим к фразам. */
export function humanizeEnrichError(reason: string | undefined, detail: string | undefined): string {
  switch (reason) {
    case 'autoteka_failed':
      return i18n.t('leads.detail.sberAutotekaFailed', { msg: detail ?? '' })
    case 'wrong_stage':
      return i18n.t('leads.detail.sberAutotekaWrongStage')
    case 'lead_has_no_vehicle_id':
    case 'lead_has_no_item_id':
      return i18n.t('leads.detail.sberAutotekaNoVehicle')
    case 'lead_not_found':
      return i18n.t('leads.detail.enrichErrNotFound')
    case 'missing_authorization':
    case 'invalid_session':
      return i18n.t('leads.detail.enrichErrSession')
    case 'forbidden':
      return i18n.t('leads.detail.enrichErrForbidden')
    case 'missing_secrets':
      return i18n.t('leads.detail.enrichErrNoSecrets')
    case 'autoteka_error':
      return i18n.t('leads.detail.enrichErrAutotekaErr')
  }
  if (detail && /non-2xx|invoke_failed|Edge Function returned/i.test(detail)) {
    return i18n.t('leads.detail.enrichErrTimeout')
  }
  return i18n.t('leads.detail.enrichErrGeneric')
}
